import { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAttachPaymentMethodMutation } from "@/store/rest_api";
import React from "react";

interface SetupFormProps {
  onSuccess: () => void;
  setAsDefault: boolean;
}

export function SetupForm({ onSuccess, setAsDefault }: SetupFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [attachPaymentMethod] = useAttachPaymentMethodMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        toast.error("Card form is not ready yet");
        setIsProcessing(false);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        toast.error(error.message);
        setIsProcessing(false);
        return;
      }

      // Attach payment method to customer via backend
      await attachPaymentMethod({
        paymentMethodId: paymentMethod.id,
        setAsDefault,
      }).unwrap();
      toast.success("Card added successfully.");
      onSuccess();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to add card");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CardElement
        options={{
          hidePostalCode: false,
          style: {
            base: {
              fontSize: "16px",
            },
          },
        }}
      />
      <Button type="submit" disabled={!stripe || isProcessing} className="w-full">
        {isProcessing ? "Adding..." : "Add Card"}
      </Button>
    </form>
  );
}
